/**
 * Finding a word in the collection ([Review](../../../docs/REVIEW.md#word-collection),
 * [D-136](../../../docs/DECISIONS.md#d-136)).
 *
 * Pure, so every rule is tested without drawing the screen.
 */

import type { CollectionStage } from '@capytalk/core';

export interface Findable {
  readonly word: string;
  readonly meaning: string;
  readonly stage: CollectionStage;
  readonly chapter: string | null;
  readonly book: number | null;
  readonly pos: string;
}

/**
 * Kinds of word, in words a nine-year-old uses.
 *
 * Four, not eight: "determiner" and "interjection" are distinctions a
 * grammarian needs and a child browsing their words does not. Everything that
 * is not a thing, a doing word or a describing word is simply "other words".
 */
export type Kind = 'thing' | 'doing' | 'describing' | 'other';

export const KIND_NAMES: Readonly<Record<Kind, string>> = {
  thing: 'Things',
  doing: 'Doing words',
  describing: 'Describing words',
  other: 'Other words',
};

export const kindOf = (pos: string): Kind => {
  if (pos === 'noun') return 'thing';
  if (pos === 'verb') return 'doing';
  if (pos === 'adjective') return 'describing';
  return 'other';
};

export interface WordFilter {
  readonly query: string;
  readonly stage: CollectionStage | null;
  readonly book: number | null;
  readonly chapter: string | null;
  readonly kind: Kind | null;
}

export const NO_FILTER: WordFilter = {
  query: '',
  stage: null,
  book: null,
  chapter: null,
  kind: null,
};

/**
 * Letters folded the way a child types them.
 *
 * Lower case, and the accents a Danish keyboard adds or drops, so searching
 * `lane` finds `låne` and `cafe` finds `café`.
 */
const folded = (text: string): string =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .trim();

/**
 * Whether a word passes every filter that is set.
 *
 * The search looks at the English word **and** the Danish meaning: a child
 * who wants to know whether they have "hund" yet should find *dog*.
 */
export function passes(word: Findable, filter: WordFilter): boolean {
  const query = folded(filter.query);
  if (
    query !== '' &&
    !folded(word.word).includes(query) &&
    !folded(word.meaning).includes(query)
  ) {
    return false;
  }
  if (filter.stage !== null && word.stage !== filter.stage) return false;
  if (filter.book !== null && word.book !== filter.book) return false;
  if (filter.chapter !== null && word.chapter !== filter.chapter) return false;
  if (filter.kind !== null && kindOf(word.pos) !== filter.kind) return false;
  return true;
}

/** Whether anything is narrowing the grid, so the screen can offer to clear it. */
export const narrowed = (filter: WordFilter): boolean =>
  filter.query.trim() !== '' ||
  filter.stage !== null ||
  filter.book !== null ||
  filter.chapter !== null ||
  filter.kind !== null;

/**
 * What each dropdown can offer, in the order the words came.
 *
 * Only values that actually occur, so no choice leads to an empty grid on
 * purpose. Chapters are limited to the chosen book.
 */
export function choices(
  words: readonly Findable[],
  book: number | null,
): {
  readonly books: readonly number[];
  readonly chapters: readonly string[];
  readonly kinds: readonly Kind[];
} {
  const books = new Set<number>();
  const chapters = new Set<string>();
  const kinds = new Set<Kind>();
  for (const word of words) {
    if (word.book !== null) books.add(word.book);
    if (word.chapter !== null && (book === null || word.book === book)) {
      chapters.add(word.chapter);
    }
    kinds.add(kindOf(word.pos));
  }
  const order: readonly Kind[] = ['thing', 'doing', 'describing', 'other'];
  return {
    books: [...books].sort((a, b) => a - b),
    chapters: [...chapters],
    kinds: order.filter((kind) => kinds.has(kind)),
  };
}
